import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil, MessageCircle, Search, Info } from 'lucide-react';
import { TopNavigation } from '@/components/layout/TopNavigation';
import { BottomNavigation } from '@/components/layout/BottomNavigation';
import { ConversationItem } from '@/components/messages/ConversationItem';
import { useConversations, Conversation } from '@/shared/hooks/useConversations';
import { useI18n } from '@/contexts/I18nContext';

const Messages: React.FC = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const { conversations, isLoading, isError, conversationsError } = useConversations();
  const { t } = useI18n();

  const filtered = useMemo(() => {
    if (!query.trim()) return conversations;
    const q = query.toLowerCase();
    return conversations.filter(
      (c) =>
        c.other_user_name?.toLowerCase().includes(q) ||
        c.other_user_username?.toLowerCase().includes(q) ||
        c.last_message_preview?.toLowerCase().includes(q),
    );
  }, [conversations, query]);

  return (
    <div className="min-h-screen w-screen min-w-full overflow-x-hidden pb-20" style={{ background: '#160C0C' }}>
      <TopNavigation />

      <main className="content-with-nav w-full">

        {/* Page header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-4">
          <h1 className="text-white font-bold text-2xl tracking-tight">{t('pages.messages.title')}</h1>
          <button
            onClick={() => navigate('/messages/new')}
            className="flex items-center justify-center w-10 h-10 rounded-full bg-[#C0392B] text-white hover:bg-[#a93226] transition-colors"
            aria-label={t('pages.messages.new_conversation')}
          >
            <Pencil className="w-4 h-4" />
          </button>
        </div>

        {/* Search bar */}
        <div className="px-4 pb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('pages.messages.search_placeholder')}
              className="w-full text-white placeholder-white/40 text-sm rounded-full pl-9 pr-4 py-2.5 outline-none focus:ring-1 focus:ring-white/20"
              style={{ background: 'rgba(255,255,255,0.08)' }}
            />
          </div>
        </div>

        {/* Retention notice */}
        {!query && (
          <div
            className="mx-4 mb-3 flex items-start gap-2.5 px-4 py-3 rounded-xl"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}
          >
            <Info className="w-4 h-4 text-white/40 flex-shrink-0 mt-0.5" />
            <p className="text-white/50 text-xs leading-relaxed">
              {t('pages.messages.retention_notice')}
            </p>
          </div>
        )}

        {/* Body */}
        <ConversationList
          conversations={filtered}
          isLoading={isLoading}
          isError={isError}
          errorMessage={(conversationsError as any)?.message}
          hasQuery={!!query.trim()}
          onSelect={(conv) => navigate(`/messages/${conv.id}`)}
          onNewConversation={() => navigate('/messages/new')}
        />
      </main>

      <BottomNavigation />
    </div>
  );
};

// ── Conversation list ─────────────────────────────────────────────────────────

interface ConversationListProps {
  conversations: Conversation[];
  isLoading: boolean;
  isError?: boolean;
  errorMessage?: string;
  hasQuery: boolean;
  onSelect: (c: Conversation) => void;
  onNewConversation: () => void;
}

const ConversationList: React.FC<ConversationListProps> = ({
  conversations,
  isLoading,
  isError,
  errorMessage,
  hasQuery,
  onSelect,
  onNewConversation,
}) => {
  const { t } = useI18n();

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-40 w-full">
        <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-2 px-6 w-full">
        <p className="text-red-400 text-sm font-medium">{t('pages.messages.error_loading')}</p>
        {errorMessage && (
          <p className="text-white/40 text-xs text-center font-mono">{errorMessage}</p>
        )}
      </div>
    );
  }

  if (conversations.length === 0) {
    if (hasQuery) {
      return (
        <p className="text-white/40 text-sm text-center py-12 px-6 w-full">
          {t('pages.messages.no_results')}
        </p>
      );
    }

    return (
      <div className="flex flex-col items-center justify-center gap-5 px-8 text-center py-24 w-full">
        <div className="w-20 h-20 rounded-full flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.08)' }}>
          <MessageCircle className="w-9 h-9 text-white/40" />
        </div>
        <div className="flex flex-col gap-2">
          <p className="text-white font-bold text-xl">{t('pages.messages.empty_title')}</p>
          <p className="text-white/50 text-sm leading-relaxed max-w-xs">
            {t('pages.messages.empty_description')}
          </p>
        </div>
        <button
          onClick={onNewConversation}
          className="px-8 py-3 rounded-full bg-[#C0392B] text-white font-semibold text-sm hover:bg-[#a93226] transition-colors"
        >
          {t('pages.messages.start_conversation')}
        </button>
      </div>
    );
  }

  return (
    <div className="divide-y divide-white/5">
      {conversations.map((conv) => (
        <ConversationItem
          key={conv.id}
          conversation={conv}
          onClick={() => onSelect(conv)}
        />
      ))}
    </div>
  );
};

export default Messages;
